import { create } from 'zustand';
import { mockStaffRoles } from '../pages/StaffRoles/MockData';
import { mockTeachers } from '../pages/Teachers/MockData';
import { StaffRole, Teacher, TeacherAvailability, TeacherPreferences } from '../types';

interface TeacherState {
  // Teachers
  teachers: Teacher[];
  staffRoles: StaffRole[];

  // Teacher CRUD
  addTeacher: (teacher: Teacher) => void;
  updateTeacher: (id: string, teacher: Partial<Teacher>) => void;
  deleteTeacher: (id: string) => void;
  getTeacher: (id: string) => Teacher | undefined;
  getTeachersBySubject: (subjectId: string) => Teacher[];
  getTeachersByRole: (role: string) => Teacher[];
  getTeachersByContractType: (contractType: Teacher['contractType']) => Teacher[];
  searchTeachers: (query: string) => Teacher[];

  // Availability management
  updateTeacherAvailability: (id: string, availability: TeacherAvailability) => void;
  getAvailableTeachers: (day: string, period: number) => Teacher[];

  // Preferences management
  updateTeacherPreferences: (id: string, preferences: TeacherPreferences) => void;

  // Staff Role CRUD
  addStaffRole: (role: StaffRole) => void;
  updateStaffRole: (id: string, role: Partial<StaffRole>) => void;
  deleteStaffRole: (id: string) => void;
  getStaffRole: (id: string) => StaffRole | undefined;
  getStaffRolesByLevel: (level: StaffRole['level']) => StaffRole[];
  getStaffRolesByTeacher: (teacherId: string) => StaffRole[];
  assignTeacherToRole: (roleId: string, teacherId: string) => void;
  unassignTeacherFromRole: (roleId: string) => void;

  // Utility functions
  getTotalTeachers: () => number;
  getTeacherWorkload: (teacherId: string) => { assigned: number; max: number; percentage: number };
  reset: () => void;
}

const initialState = {
  teachers: mockTeachers,
  staffRoles: mockStaffRoles,
};

export const useTeacherStore = create<TeacherState>((set, get) => ({
  ...initialState,

  // Teacher CRUD
  addTeacher: (teacher) =>
    set((state) => ({
      teachers: [...state.teachers, teacher],
    })),

  updateTeacher: (id, teacher) =>
    set((state) => ({
      teachers: state.teachers.map((t) => (t.id === id ? { ...t, ...teacher } : t)),
    })),

  deleteTeacher: (id) =>
    set((state) => ({
      teachers: state.teachers.filter((t) => t.id !== id),
      staffRoles: state.staffRoles.map((role) =>
        role.assignedTeacherId === id ? { ...role, assignedTeacherId: undefined } : role,
      ),
    })),

  getTeacher: (id) => get().teachers.find((t) => t.id === id),

  getTeachersBySubject: (subjectId) => get().teachers.filter((t) => t.subjects.includes(subjectId)),

  getTeachersByRole: (role) => get().teachers.filter((t) => t.roles.includes(role as any)),

  getTeachersByContractType: (contractType) => get().teachers.filter((t) => t.contractType === contractType),

  searchTeachers: (query) => {
    const lowerQuery = query.toLowerCase();
    return get().teachers.filter(
      (t) =>
        t.firstName.toLowerCase().includes(lowerQuery) ||
        t.lastName.toLowerCase().includes(lowerQuery) ||
        t.email.toLowerCase().includes(lowerQuery) ||
        t.subjects.some((s) => s.toLowerCase().includes(lowerQuery)),
    );
  },

  // Availability management
  updateTeacherAvailability: (id, availability) =>
    set((state) => ({
      teachers: state.teachers.map((t) => (t.id === id ? { ...t, availability } : t)),
    })),

  getAvailableTeachers: (day, period) =>
    get().teachers.filter((t) => {
      const dayKey = day as 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday';
      const dayAvailability = t.availability[dayKey];
      return dayAvailability?.available && dayAvailability.periods.includes(period);
    }),

  // Preferences management
  updateTeacherPreferences: (id, preferences) =>
    set((state) => ({
      teachers: state.teachers.map((t) => (t.id === id ? { ...t, preferences } : t)),
    })),

  // Staff Role CRUD
  addStaffRole: (role) =>
    set((state) => ({
      staffRoles: [...state.staffRoles, role],
    })),

  updateStaffRole: (id, role) =>
    set((state) => ({
      staffRoles: state.staffRoles.map((r) => (r.id === id ? { ...r, ...role } : r)),
    })),

  deleteStaffRole: (id) =>
    set((state) => ({
      staffRoles: state.staffRoles.filter((r) => r.id !== id),
    })),

  getStaffRole: (id) => get().staffRoles.find((r) => r.id === id),

  getStaffRolesByLevel: (level) => get().staffRoles.filter((r) => r.level === level),

  getStaffRolesByTeacher: (teacherId) => get().staffRoles.filter((r) => r.assignedTeacherId === teacherId),

  assignTeacherToRole: (roleId, teacherId) =>
    set((state) => ({
      staffRoles: state.staffRoles.map((r) => (r.id === roleId ? { ...r, assignedTeacherId: teacherId } : r)),
    })),

  unassignTeacherFromRole: (roleId) =>
    set((state) => ({
      staffRoles: state.staffRoles.map((r) => (r.id === roleId ? { ...r, assignedTeacherId: undefined } : r)),
    })),

  // Utility functions
  getTotalTeachers: () => get().teachers.length,

  getTeacherWorkload: (teacherId) => {
    const teacher = get().getTeacher(teacherId);
    if (!teacher) {
      return { assigned: 0, max: 0, percentage: 0 };
    }

    // This would normally calculate from actual timetable assignments
    // For now, using a mock calculation
    const assigned = Math.floor(Math.random() * teacher.maxPeriodsPerWeek);
    const max = teacher.maxPeriodsPerWeek;
    const percentage = Math.round((assigned / max) * 100);

    return { assigned, max, percentage };
  },

  reset: () => set(initialState),
}));
